import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Mic } from 'lucide-react';
import VoiceRecorderBar from './VoiceRecorderBar';
import { useVoiceRecorder, VoiceRecorderError } from '../../hooks/useVoiceRecorder';
import { MIN_RECORDING_MS, audioFileName, isVoiceRecordingSupported } from '../../utils/audio';
import { readBlobAsDataUrl } from '../../utils/dataUrl';

interface SelectedFile {
  dataUrl: string;
  type: string;
  name: string;
}

// An attachment handed to the provider: a picked image/PDF, or a voice note
// (audio/* with its recorded length).
export interface ComposerFile {
  dataUrl: string;
  type: string;
  name: string;
  durationMillis?: number;
}

interface ChatInputProps {
  disabled: boolean;
  onSend: (text: string, file?: ComposerFile) => void;
  // Rendered at the top of the input box (the patient focus pill).
  header?: React.ReactNode;
}

// Copy for what stopped a voice note, in the composer's error slot. Says what
// did not happen and what to do (docs/ui-guidelines.md).
const VOICE_ERROR_TEXT: Record<VoiceRecorderError | 'too_short' | 'discarded', string> = {
  unsupported: 'Este navegador no puede grabar notas de voz.',
  permission_denied: 'No hay acceso al micrófono. Permítelo en el navegador y vuelve a intentarlo.',
  no_device: 'No se encontró ningún micrófono.',
  too_large: 'Esa grabación es demasiado larga para enviarla. Prueba con una más corta.',
  failed: 'Algo ha fallado con la grabación. Inténtalo de nuevo.',
  too_short: 'Demasiado corta. Las notas de voz deben durar al menos 3 segundos.',
  discarded: 'La grabación se descartó al cambiar de pestaña o perder la conexión. Graba la nota de nuevo.',
};

// Mirror the backend gate: images and PDFs only, max 10 MB (base64 encoding
// happens server-side, so we measure the raw file size here).
const MAX_FILE_BYTES = 10 * 1024 * 1024;
const isAllowedType = (type: string): boolean =>
  type.startsWith('image/') || type === 'application/pdf';

const ChatInput: React.FC<ChatInputProps> = ({ disabled, onSend, header }) => {
  const [text, setText] = useState('');
  const [file, setFile] = useState<SelectedFile | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Nested elements fire dragenter/dragleave as the pointer moves within the
  // drop zone; counting enters vs. leaves keeps the highlight from flickering.
  const dragDepthRef = useRef(0);

  const canSend = !disabled && (text.trim() !== '' || file !== null);
  const isPdf = file?.type === 'application/pdf';

  // Voice notes. Probed once: the answer doesn't change while the page lives.
  const voiceSupported = useMemo(() => isVoiceRecordingSupported(), []);
  // The cap stops the recorder; the composer then sends what was captured.
  const stopRecordingRef = useRef<() => void>(() => {});
  const recorder = useVoiceRecorder({ onMaxDuration: () => stopRecordingRef.current() });
  const recorderActive = recorder.phase !== 'idle';
  // Why the last note was not sent: the composer's own refusals (too short)
  // or what stopped the recorder (permission, no device, encoding).
  const [composerVoiceError, setComposerVoiceError] = useState<string | null>(null);
  const voiceError = composerVoiceError ?? (recorder.error ? VOICE_ERROR_TEXT[recorder.error] : null);

  const startRecording = async () => {
    setError(null);
    setComposerVoiceError(null);
    await recorder.start();
  };

  // Audio only travels over the socket, so the note is sent the moment it is
  // stopped, as the app does: text '' and the recording as the attachment.
  const stopAndEncode = recorder.stopAndEncode;
  const stopRecording = useCallback(async () => {
    const recording = await stopAndEncode();
    if (!recording) return;
    if (recording.durationMillis < MIN_RECORDING_MS) {
      setComposerVoiceError(VOICE_ERROR_TEXT.too_short);
      return;
    }
    onSend('', {
      dataUrl: recording.dataUrl,
      type: recording.mimeType,
      name: audioFileName(recording.mimeType),
      durationMillis: recording.durationMillis,
    });
  }, [stopAndEncode, onSend]);

  useEffect(() => {
    stopRecordingRef.current = stopRecording;
  }, [stopRecording]);

  // Losing the composer (socket closed, turn in flight) or hiding the tab
  // discards the note rather than leaving the microphone live.
  const cancelRecording = recorder.cancel;
  // Not the clinician's own discard: say that the note was lost, or they keep
  // talking to a dead microphone.
  const dropRecording = useCallback(() => {
    if (cancelRecording()) setComposerVoiceError(VOICE_ERROR_TEXT.discarded);
  }, [cancelRecording]);
  useEffect(() => {
    if (disabled && recorderActive) dropRecording();
  }, [disabled, recorderActive, dropRecording]);
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') dropRecording();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [dropRecording]);

  const submit = () => {
    if (!canSend) return;
    onSend(text.trim(), file ? { dataUrl: file.dataUrl, type: file.type, name: file.name } : undefined);
    setText('');
    setFile(null);
    setError(null);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  const acceptFile = async (picked: File) => {
    if (!isAllowedType(picked.type)) {
      setError('Solo se admiten imágenes y archivos PDF.');
      return;
    }
    if (picked.size > MAX_FILE_BYTES) {
      setError('El archivo es demasiado grande. Tamaño máximo: 10 MB.');
      return;
    }
    setError(null);
    try {
      const dataUrl = await readBlobAsDataUrl(picked);
      setFile({ dataUrl, type: picked.type, name: picked.name });
    } catch (err) {
      console.warn('ChatInput: failed to read file', err);
      setError('No se pudo leer el archivo.');
    }
  };

  const onPickFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = e.target.files?.[0];
    // Reset so picking the same file again still fires onChange.
    e.target.value = '';
    if (picked) acceptFile(picked);
  };

  const onDragEnter = (e: React.DragEvent) => {
    if (disabled || !e.dataTransfer.types.includes('Files')) return;
    e.preventDefault();
    dragDepthRef.current += 1;
    setDragActive(true);
  };

  const onDragOver = (e: React.DragEvent) => {
    if (disabled || !e.dataTransfer.types.includes('Files')) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  const onDragLeave = (e: React.DragEvent) => {
    if (disabled) return;
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) setDragActive(false);
  };

  const onDrop = (e: React.DragEvent) => {
    if (disabled) return;
    e.preventDefault();
    dragDepthRef.current = 0;
    setDragActive(false);
    // Only the first file — the composer attaches one at a time.
    const dropped = e.dataTransfer.files?.[0];
    if (dropped) acceptFile(dropped);
  };

  return (
    <div
      onDragEnter={onDragEnter}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      style={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        padding: '12px 12px 20px',
        borderTop: '1px solid var(--border)',
        background: 'var(--card)',
      }}
    >
      {dragActive && (
        <div
          style={{
            position: 'absolute',
            inset: 4,
            zIndex: 5,
            borderRadius: 12,
            border: '2px dashed var(--primary)',
            background: 'rgba(56,178,172,0.08)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 13,
            fontWeight: 600,
            color: 'var(--primary)',
            pointerEvents: 'none',
          }}
        >
          Suelta una imagen o PDF
        </div>
      )}

      {header}

      {file && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ position: 'relative' }}>
            {isPdf ? (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  maxWidth: 220,
                  padding: '8px 10px',
                  borderRadius: 8,
                  border: '1px solid var(--border)',
                  background: 'var(--muted)',
                }}
              >
                <span aria-hidden="true" style={{ fontSize: 20 }}>📄</span>
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: 'var(--foreground)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  title={file.name}
                >
                  {file.name}
                </span>
              </div>
            ) : (
              <img
                src={file.dataUrl}
                alt={file.name}
                style={{
                  width: 56,
                  height: 56,
                  objectFit: 'cover',
                  borderRadius: 8,
                  border: '1px solid var(--border)',
                }}
              />
            )}
            <button
              onClick={() => setFile(null)}
              aria-label="Quitar archivo"
              style={{
                position: 'absolute',
                top: -6,
                right: -6,
                width: 18,
                height: 18,
                borderRadius: '50%',
                border: 'none',
                background: 'var(--foreground)',
                color: 'var(--card)',
                fontSize: 12,
                lineHeight: 1,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              ×
            </button>
          </div>
        </div>
      )}

      {error && (
        <div style={{ fontSize: 12, color: 'var(--destructive)' }}>{error}</div>
      )}
      {voiceError && (
        <div role="alert" style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--destructive)' }}>
          <span>{voiceError}</span>
          {!recorderActive && !disabled && (
            <button
              type="button"
              onClick={startRecording}
              style={{
                border: 'none',
                background: 'transparent',
                padding: 0,
                color: 'var(--destructive)',
                fontSize: 12,
                fontWeight: 700,
                textDecoration: 'underline',
                cursor: 'pointer',
              }}
            >
              Reintentar
            </button>
          )}
        </div>
      )}

      {recorder.phase !== 'idle' ? (
        <VoiceRecorderBar
          phase={recorder.phase}
          durationMillis={recorder.durationMillis}
          level={recorder.level}
          onStop={stopRecording}
          onCancel={cancelRecording}
        />
      ) : (
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,application/pdf"
          onChange={onPickFile}
          style={{ display: 'none' }}
        />
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled}
          aria-label="Adjuntar archivo"
          title="Adjuntar imagen o PDF"
          style={{
            border: '1px solid var(--border)',
            borderRadius: 10,
            width: 38,
            height: 38,
            flexShrink: 0,
            background: 'var(--card)',
            color: disabled ? 'var(--muted-foreground)' : 'var(--primary)',
            fontSize: 18,
            cursor: disabled ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          📎
        </button>
        {voiceSupported && (
          <button
            type="button"
            onClick={startRecording}
            disabled={disabled}
            aria-label="Grabar una nota de voz"
            title="Grabar una nota de voz"
            style={{
              border: '1px solid var(--border)',
              borderRadius: 10,
              width: 38,
              height: 38,
              flexShrink: 0,
              background: 'var(--card)',
              color: disabled ? 'var(--muted-foreground)' : 'var(--primary)',
              cursor: disabled ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Mic size={18} aria-hidden="true" />
          </button>
        )}

        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKeyDown}
          disabled={disabled}
          placeholder="Escribe al asistente…"
          aria-label="Mensaje para el asistente"
          rows={1}
          style={{
            flex: 1,
            resize: 'none',
            maxHeight: 120,
            minHeight: 38,
            padding: '9px 12px',
            borderRadius: 10,
            border: '1px solid var(--border)',
            fontFamily: 'inherit',
            fontSize: 14,
            lineHeight: 1.4,
            outline: 'none',
            color: disabled ? 'var(--muted-foreground)' : 'var(--foreground)',
            background: disabled ? 'var(--border)' : 'var(--card)',
            cursor: disabled ? 'not-allowed' : 'text',
          }}
        />
        <button
          onClick={submit}
          disabled={!canSend}
          style={{
            border: 'none',
            borderRadius: 10,
            padding: '9px 16px',
            height: 38,
            background: canSend ? 'var(--primary)' : 'var(--primary-subtle)',
            color: 'var(--card)',
            fontWeight: 600,
            fontSize: 14,
            cursor: canSend ? 'pointer' : 'default',
          }}
        >
          Enviar
        </button>
      </div>
      )}
    </div>
  );
};

export default ChatInput;
